import { useQuery } from "@tanstack/react-query";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useEmailNotifications } from "@/hooks/useEmailNotifications";
import { useNoteNotifications } from "@/hooks/useNoteNotifications";
import { useNavigate } from "react-router-dom";

// Sprint 30 — Bell icon in header. Click to opt-in / opt-out of browser
// notifications for new client emails.
export default function NotificationBell() {
  const navigate = useNavigate();
  const { data: cfg } = useQuery({
    queryKey: ["email-config"],
    queryFn: async () => (await api.get("/email/config")).data,
  });
  const hasImapConfig = !!cfg;

  const { enabled, permission, supported, requestPermission, disable } =
    useEmailNotifications({ hasImapConfig });

  const active = enabled && permission === "granted";
  const { dueCount } = useNoteNotifications({ enabled: supported && hasImapConfig && active });

  if (!supported || !hasImapConfig) return null;

  const onClick = async () => {
    if (dueCount > 0) {
      navigate("/notizen");
      return;
    }
    if (active) {
      disable();
      toast.success("E-Mail-Benachrichtigungen deaktiviert");
    } else if (permission === "denied") {
      toast.error("Benachrichtigungen sind blockiert. Bitte in den Browser-Einstellungen aktivieren.");
    } else {
      const p = await requestPermission();
      if (p === "granted") toast.success("Benachrichtigungen aktiviert — Sie werden bei neuen E-Mails informiert.");
      else if (p === "denied") toast.error("Berechtigung verweigert.");
    }
  };

  const Icon = active ? BellRing : permission === "denied" ? BellOff : Bell;
  const title = active
    ? "Benachrichtigungen aktiv (klicken zum Deaktivieren)"
    : permission === "denied"
      ? "Benachrichtigungen blockiert"
      : "Benachrichtigungen für neue E-Mails aktivieren";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="notification-bell"
      title={title}
      aria-label={title}
      className={`relative inline-flex items-center justify-center h-9 w-9 rounded-md border transition-colors ${
        active
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border hover:bg-muted text-muted-foreground"
      }`}
    >
      <Icon className="h-4 w-4" />
      {dueCount > 0 && (
        <span className="absolute -right-1.5 -top-1.5 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white shadow-sm">
          {dueCount > 9 ? "9+" : dueCount}
        </span>
      )}
    </button>
  );
}
